import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddSupportCommentInput,
  ChangeSupportStatusInput,
  CreateSupportTicketInput,
  Paginated,
  SupportTicketDetail,
  SupportTicketSummary,
  UpdateSupportTicketInput,
} from '@asyagrup/group-hub-contracts';
import { api } from '@/lib/api/client';

/**
 * Tek kök: bir kayıt üzerinde yapılan her şey hem listeyi hem ayrıntıyı aynı
 * anda yanlış kılar (durum değişir, yorum sayısı artar), o yüzden tek önek
 * geçersiz kılma tam olarak istenen şey.
 */
export const supportKeys = {
  all: ['support'] as const,
  list: (query: string) => ['support', 'list', query] as const,
  detail: (id: string) => ['support', 'detail', id] as const,
};

export function useSupportTickets(params: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
  const qs = search.toString();
  return useQuery({
    queryKey: supportKeys.list(qs),
    queryFn: ({ signal }) =>
      api.get<Paginated<SupportTicketSummary>>(`/support/tickets${qs ? `?${qs}` : ''}`, signal),
  });
}

export function useSupportTicket(id: string | null) {
  return useQuery({
    queryKey: supportKeys.detail(id ?? ''),
    enabled: id !== null,
    queryFn: ({ signal }) => api.get<SupportTicketDetail>(`/support/tickets/${id}`, signal),
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: supportKeys.all });
}

export function useCreateTicket() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateSupportTicketInput) =>
      api.post<SupportTicketDetail>('/support/tickets', input),
    onSuccess: invalidate,
  });
}

export function useChangeStatus(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: ChangeSupportStatusInput) =>
      api.post<SupportTicketDetail>(`/support/tickets/${id}/status`, input),
    onSuccess: invalidate,
  });
}

export function useAddComment(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: AddSupportCommentInput) =>
      api.post<SupportTicketDetail>(`/support/tickets/${id}/comments`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateTicket(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: UpdateSupportTicketInput) =>
      api.patch<SupportTicketDetail>(`/support/tickets/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useUploadAttachment(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ file, commentId }: { file: File; commentId?: string }) => {
      const form = new FormData();
      // `commentId` DOSYADAN ÖNCE: @fastify/multipart alanları sırayla okuyor
      // ve dosyadan sonra gelen bir alan sunucuda hiç görünmüyor.
      if (commentId) form.append('commentId', commentId);
      form.append('file', file);
      return api.postForm<SupportTicketDetail>(`/support/tickets/${id}/upload`, form);
    },
    onSuccess: invalidate,
  });
}
